import React, { useState, useEffect, useLayoutEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import RateReviewModal from "./RateReviewModal";
import { BASE_URL, UPLOAD_URL } from "../../config/config";


const ProfileOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [itemRatings, setItemRatings] = useState({});
  const [apiOrder, setApiOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // strip ORD prefix for backend
  const rawOrderId = orderId?.startsWith("ORD") ? orderId.slice(3) : orderId;

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token"); // adjust if you use a different key
        const res = await fetch(`${BASE_URL}/order/${rawOrderId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });
        

        const json = await res.json();
        // your API returns { status: 200, data: [ {...} ] }
        if (json && json.status === 200 && Array.isArray(json.data) && json.data.length > 0) {
          setApiOrder(json.data[0]);
        } else {
          // If API returns single object without wrapper, try to use it directly
          if (json && json.data && !Array.isArray(json.data)) {
            setApiOrder(json.data);
          } else {
            setApiOrder(null);
            console.warn("Order not found or unexpected response:", json);
          }
        }
      } catch (err) {
        console.error("Error fetching order:", err);
        setApiOrder(null);
      } finally {
        setLoading(false);
      }
    };

    if (rawOrderId) fetchOrder();
  }, [rawOrderId]);

  // while loading or no order yet
  if (loading) {
    return (
      <main className="ml-5 w-[73%] max-md:ml-0 max-md:w-full">
        <div className="flex justify-center items-center h-64">
          <p className="text-sky-950">Loading order details...</p>
        </div>
      </main>
    );
  }

  if (!apiOrder) {
    return (
      <main className="ml-5 w-[73%] max-md:ml-0 max-md:w-full">
        <div className="flex justify-center items-center h-64">
          <p className="text-red-500">Order not found</p>
        </div>
      </main>
    );
  }

  // Map API response into the same shape your original UI expects (keeping design same)
  const orderData = {
    id: `#ORD${apiOrder.order_id || rawOrderId}`, // keep the same display id format
    items: (apiOrder.products || []).map((p) => ({
      name: p.product_name,
      quantity: p.quantity,
      image: `${UPLOAD_URL}/products/${p.image}`, // adjust if your uploads path is different
      product_id: p.product_id,
      price: p.price,
      offer_price: p.offer_price,
    })),
    status: (apiOrder.order_status || "Pending"),
    orderDate: apiOrder.create_audit_time ? `Ordered on ${apiOrder.create_audit_time}` : "Ordered on -",
    deliveredDate: apiOrder.delivered_audit_time || apiOrder.deliveredDate || null,
    returnDate: apiOrder.return_date || apiOrder.returnedDate || null,
    returnedDate: apiOrder.returnedDate || apiOrder.returned_date || null,
  };

  // Create unique item identifier
  const getItemId = (item, index) => {
    return `${item.name}_${index}_${item.image}`;
  };

  // Handle review submission
  const handleReviewSubmit = (reviewData) => {
    setItemRatings((prev) => ({
      ...prev,
      [reviewData.itemId]: reviewData.rating,
    }));
  };

  // Create order data structure for the component (same keys as your original code)
  // const subTotal = parseFloat(apiOrder.sub_total || apiOrder.total_amount || "0");
  // const shipping = parseFloat(apiOrder.shipping_charge || "15");
  // const discount = parseFloat(apiOrder.discount || "0");
  // const calculatedTotal = subTotal + shipping - discount;

  // helpers (inline + tiny)
const toNum = (v) => Number(String(v ?? 0).replace(/[^0-9.]/g, "")) || 0;
const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// derive items from apiOrder.products
const items = (apiOrder.products || []).map((p, i) => {
  const unit = toNum(p.offer_price ?? p.price);
  const qty  = toNum(p.quantity || 1);
  return {
    id: p.product_id ?? i + 1,
    name: p.product_name || "Unnamed Product",
    image: p.image
      ? `${UPLOAD_URL}/products/${p.image}`
      : p.product_image || "fallback-image-url",
    quantity: qty,
    price: inr(unit),
    priceValue: unit,
    status: apiOrder.order_status,
    returnDate: null,
  };
});

// compute amounts
const subtotal = items.reduce((s, it) => s + it.priceValue * it.quantity, 0);
const shipping = toNum(apiOrder.shipping_charge) || 0;

// tax heuristic: treat small numbers (e.g., 18) as percentage
const taxRaw = toNum(apiOrder.tax);
const taxAmount =
  taxRaw && taxRaw <= 40 ? Math.round(subtotal * (taxRaw / 100)) : taxRaw;

const total = subtotal + shipping + taxAmount;

const formattedOrderData = {
  orderInfo: {
    orderId: apiOrder.order_id ?? apiOrder.id ?? "N/A",
    orderedAt: apiOrder.create_audit_time
      ? new Date(apiOrder.create_audit_time.replace(" ", "T")).toLocaleString(
          "en-GB",
          { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
        )
      : "N/A",
    status: apiOrder.order_status ?? "placed",
    payment: apiOrder.payment_mode ?? "Card",
    referralCode: apiOrder.referralCode || "N/A",
  },
  deliveryAddress: {
    name: apiOrder.address_name || "N/A",
    address:
      apiOrder.address_line && apiOrder.city && apiOrder.address_pin
        ? `${apiOrder.address_line}, ${apiOrder.city}, ${apiOrder.address_pin}`
        : apiOrder.address_line || "N/A",
    phone: apiOrder.address_phone || apiOrder.user_mobile || "N/A",
  },

  items,

  orderSummary: {
    premiumChargeLabel: "Order Sub Total",
    premiumCharge: inr(subtotal),

    shippingChargeLabel: "Shipping charge",
    shippingCharge: inr(shipping),

    discountLabel: taxRaw && taxRaw <= 40 ? `Tax (${taxRaw}%)` : "Tax",
    discount: inr(taxAmount),

    total: inr(total),
  },

  actions: [
    {
      label: "Rate & Review",
      primary: false,
      onClick: () => setIsModalOpen(true),
      hideWhenAllRated: true,
    },
  ],
};




  // Check if all items have been rated
  const areAllItemsRated = () => {
    return formattedOrderData.items.every((item) => itemRatings[item.id]);
  };

  const getStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "returned":
        return "text-fuchsia-500";
      case "delivered":
        return "text-green-500";
      case "pending":
        return "text-yellow-500";
      case "cancelled":
        return "text-red-500";
      case "shipped":
        return "text-indigo-400";
      case "placed":
        return "text-red-500";
      default:
        return "text-sky-950";
    }
  };

  const getItemStatusColor = (status) => {
    switch ((status || "").toLowerCase()) {
      case "returned":
        return "text-sky-950 opacity-70";
      case "delivered":
        return "text-green-500";
      case "shipped":
        return "text-indigo-400";
      case "placed":
        return "text-red-500";
      default:
        return "text-sky-950 opacity-70";
    }
  };

  const renderItemContent = (item) => {
    const status = (item.status || "").toLowerCase();
    const itemRating = itemRatings[item.id];

    if (status === "placed" || status === "shipped") {
      return (
        <>
          <div className="text-base text-sky-950">{item.name}</div>
          <div className="flex items-center self-start text-sm whitespace-nowrap text-sky-950 w-full relative">
            <span className="self-stretch my-auto text-sky-950 opacity-70">
              Quantity : {item.quantity}
            </span>
            {itemRating && (
              <div className="flex items-center gap-1 absolute right-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fbbf24" className="flex-shrink-0">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="#f59e0b"
                    strokeWidth="0.5"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm font-medium text-sky-950">{itemRating}</span>
              </div>
            )}
          </div>
        </>
      );
    } else if (status === "delivered") {
      return (
        <>
          <div className="text-base text-sky-950">
            {item.quantity} X {item.name}
          </div>
          <div className="flex items-center self-start text-sm whitespace-nowrap text-sky-950 w-full relative">
            <span className="self-stretch my-auto text-sky-950 opacity-70">Return: {item.returnDate || "N/A"}</span>
            {itemRating && (
              <div className="flex items-center gap-1 absolute right-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fbbf24" className="flex-shrink-0">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="#f59e0b"
                    strokeWidth="0.5"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm font-medium text-sky-950">{itemRating}</span>
              </div>
            )}
          </div>
        </>
      );
    } else if (status === "returned") {
      return (
        <>
          <div className="text-base text-sky-950">
            {item.quantity} X {item.name}
          </div>
          <div className="flex items-center self-start text-sm whitespace-nowrap text-sky-950 w-full relative">
            <span className="self-stretch my-auto text-sky-950 opacity-70">Returned</span>
            {itemRating && (
              <div className="flex items-center gap-1 absolute right-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fbbf24" className="flex-shrink-0">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="#f59e0b"
                    strokeWidth="0.5"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm font-medium text-sky-950">{itemRating}</span>
              </div>
            )}
          </div>
        </>
      );
    } else {
      return (
        <>
          <div className="text-base text-sky-950">
            {item.quantity} X {item.name}
          </div>
          <div className="flex items-center self-start text-sm whitespace-nowrap text-sky-950 w-full relative">
            <span className={`self-stretch my-auto ${getItemStatusColor(item.status)}`}>{item.status}</span>
            {itemRating && (
              <div className="flex items-center gap-1 absolute right-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#fbbf24" className="flex-shrink-0">
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                    stroke="#f59e0b"
                    strokeWidth="0.5"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-sm font-medium text-sky-950">{itemRating}</span>
              </div>
            )}
          </div>
        </>
      );
    }
  };

  const getVisibleActions = () => {
    const currentStatus = (formattedOrderData.orderInfo.status || "").toLowerCase();
    return formattedOrderData.actions.filter((action) => {
      if (action.hideWhenAllRated && areAllItemsRated()) {
        return false;
      }
      if (action.hideForStatus) {
        return !action.hideForStatus.includes(currentStatus);
      }
      return true;
    });
  };

  return (
    <main className="ml-5 w-[73%] max-md:ml-0 max-md:w-full">
      <div className="flex flex-col grow items-center max-md:mt-9 max-md:max-w-full">
        {/* Order Information Section */}
        <section className="max-w-full rounded-none w-[804px]">
          <div className="flex flex-col px-5 py-4 w-full bg-white rounded-2xl shadow-2xl max-md:max-w-full">
            <h2 className="self-start text-lg font-semibold leading-none text-sky-950">
              Order Information
            </h2>
            <div className="shrink-0 mt-2.5 h-px border border-solid border-neutral-300 max-md:max-w-full" />

            <div className="flex flex-wrap gap-1.5 justify-between items-start mt-3 w-full text-base max-md:max-w-full">
              {/* Order ID */}
              <div className="flex justify-between rounded-none min-w-60 text-sky-950 w-[307px]">
                <span className="opacity-50 text-sky-950">Order ID</span>
                <span className="font-medium text-sky-950">
                  {formattedOrderData.orderInfo.orderId}
                </span>
              </div>

              {/* Ordered At */}
              <div className="flex justify-between rounded-none min-w-60 text-sky-950 w-[307px]">
                <span className="opacity-50 text-sky-950">Ordered At</span>
                <span className="font-medium text-sky-950">
                  {formattedOrderData.orderInfo.orderedAt}
                </span>
              </div>

              {/* Status */}
              <div className="flex justify-between whitespace-nowrap rounded-none min-w-60 w-[307px]">
                <span className="opacity-50 text-sky-950">Status</span>
                <span
                  className={`font-medium ${getStatusColor(
                    formattedOrderData.orderInfo.status
                  )}`}
                >
                  {formattedOrderData.orderInfo.status}
                </span>
              </div>

              {/* Payment */}
              <div className="flex justify-between whitespace-nowrap rounded-none min-w-60 text-sky-950 w-[307px]">
                <span className="opacity-50 text-sky-950">Payment</span>
                <span className="font-medium text-sky-950">
                  {formattedOrderData.orderInfo.payment}
                </span>
              </div>

              {/* Referral Code (NEW) */}
              <div className="flex justify-between whitespace-nowrap rounded-none min-w-60 text-sky-950 w-[307px]">
                <span className="opacity-50 text-sky-950">Referral Code</span>
                <span className="font-medium text-sky-950">
                  {formattedOrderData.orderInfo.referralCode}
                </span>
              </div>
            </div>
          </div>
        </section>


        {/* Delivery Address Section */}
        <section className="mt-4 max-w-full text-base font-semibold rounded-none text-sky-950 w-[804px]">
          <div className="flex flex-col items-start px-5 py-4 w-full bg-white rounded-2xl shadow-2xl max-md:max-w-full">
            <h2 className="text-lg leading-none text-sky-950"></h2>
            <div className="shrink-0 self-stretch mt-2.5 w-full h-px border border-solid border-neutral-300" />
            <div className="flex gap-2.5 items-center mt-2">
              <span className="self-stretch my-auto text-sky-950">{formattedOrderData.deliveryAddress.name}</span>
            </div>
            <address className="mt-1.5 tracking-normal text-black opacity-60 max-md:max-w-full not-italic">{formattedOrderData.deliveryAddress.address}</address>
            <div className="mt-1.5 text-sm tracking-normal text-black opacity-60">{formattedOrderData.deliveryAddress.phone}</div>
          </div>
        </section>

        {/* Items Section */}
        <section className="mt-4 max-w-full font-medium rounded-none w-[804px]">
          <div className="flex flex-col items-start px-5 py-4 bg-white rounded-2xl shadow-2xl max-md:max-w-full">
            <h2 className="text-lg font-semibold leading-none text-sky-950">Items</h2>
            <div className="shrink-0 self-stretch mt-2.5 w-full h-px border border-solid border-neutral-300" />
            <div className="flex flex-col items-start mt-2.5 max-w-full w-[732px] gap-3.5">
              {formattedOrderData.items.map((item, index) => (
                <div key={index} className="flex gap-3 max-w-full rounded-none w-full">
                  <img src={item.image} alt={item.name} className="object-contain shrink-0 self-start rounded-md aspect-square w-[46px]" />
                  <div className="flex flex-col grow shrink-0 basis-0 w-fit">{renderItemContent(item)}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Order Summary Section */}
        <section className="mt-4 max-w-full text-lg rounded-2xl w-[804px]">
          <div className="flex flex-col px-5 py-4 w-full bg-white rounded-2xl shadow-2xl max-md:max-w-full">
            <h2 className="self-start font-semibold leading-none text-sky-950">Order Summary</h2>
            <div className="shrink-0 mt-2.5 max-w-full h-px border border-solid border-neutral-300 w-[764px]" />
            <div className="mt-4 space-y-3 text-base">
            {[
              {
                label: formattedOrderData.orderSummary.premiumChargeLabel,
                value: formattedOrderData.orderSummary.premiumCharge,
                color: "text-sky-950",
              },
              {
                label: formattedOrderData.orderSummary.shippingChargeLabel,
                value: formattedOrderData.orderSummary.shippingCharge,
                color: "text-sky-950",
              }
              ,
              {
                label: formattedOrderData.orderSummary.discountLabel,
                value: formattedOrderData.orderSummary.discount,
                color: "text-sky-950",
              },
            ].map((row) => (
              <div key={row.label} className="flex items-baseline justify-between">
                <span className="text-sky-950/90">{row.label}</span>
                {/* fixed width + right align + tabular numbers for perfect column alignment */}
                <span className={`w-32 md:w-40 text-right font-medium tabular-nums ${row.color}`}>
                  {row.value}
                </span>
              </div>
            ))}
          </div>
            <div className="shrink-0 mt-4 max-w-full h-px border border-solid border-neutral-200 w-[764px]" />
            <div className="flex flex-wrap gap-5 justify-between mt-5 font-semibold tracking-normal whitespace-nowrap text-sky-950 max-md:max-w-full">
              <span className="text-sky-950">Total</span>
              <span className="text-right text-sky-950">{formattedOrderData.orderSummary.total}</span>
            </div>
          </div>
        </section>

        {/* Action Buttons */}
        {getVisibleActions().length > 0 && (
          <div className="flex flex-wrap gap-5 justify-center items-center mt-5 max-w-full text-base font-semibold tracking-wide leading-tight text-center w-[761px] max-md:mr-2.5">
            {getVisibleActions().map((action, index) => (
              <button
                key={index}
                className={`flex gap-10 justify-center items-center px-5 py-4 rounded-2xl border-b border-white border-opacity-30 min-h-[50px] transition-all duration-200 ${
                  action.primary ? "text-white bg-green-500 shadow-lg border-b-white border-b-opacity-30" : "text-neutral-600 bg-zinc-300 hover:text-white hover:bg-green-500 hover:shadow-lg"
                }`}
                onClick={action.onClick}
                disabled={action.disabled}
                aria-label={action.ariaLabel || action.label}
              >
                <span className={action.primary ? "text-white" : ""}>{action.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Rate & Review Modal */}
      <RateReviewModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} items={formattedOrderData.items} onSubmitReview={handleReviewSubmit} itemRatings={itemRatings} />
    </main>
  );
};

export default ProfileOrderDetails;
